import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fiscalCategoriesService } from '../services/api';
import { toast } from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  FolderOpen,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  ArrowUp,
  ArrowDown,
  Tag
} from 'lucide-react';

const FiscalCategoriesManager = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    document_types: '',
    display_order: 0
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const queryClient = useQueryClient();

  // Query para buscar categorias
  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['fiscal-categories'],
    queryFn: () => fiscalCategoriesService.getCategories({ active: true })
  });

  // Mutation para criar/atualizar
  const saveMutation = useMutation({
    mutationFn: editingCategory
      ? ({ id, data }) => fiscalCategoriesService.updateCategory(id, data)
      : fiscalCategoriesService.createCategory,
    onSuccess: () => {
      toast.success(editingCategory ? 'Categoria atualizada!' : 'Categoria criada!');
      queryClient.invalidateQueries(['fiscal-categories']);
      closeModal();
    },
    onError: (error) => {
      // Só mostra erro se realmente houve falha HTTP
      if (error.response?.status !== 201 && error.response?.status !== 200) {
        toast.error(error.response?.data?.message || 'Erro ao salvar');
      }
    }
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: fiscalCategoriesService.deleteCategory,
    onSuccess: () => {
      toast.success('Categoria removida!');
      queryClient.invalidateQueries(['fiscal-categories']);
    },
    onError: (error) => {
      if (error.response?.status !== 200 && error.response?.status !== 204) {
        toast.error(error.response?.data?.message || 'Erro ao remover');
      }
    }
  });

  const categories = categoriesData?.data?.data?.categories || [];

  const handleSubmit = (e) => {
    e.preventDefault();

    const data = {
      ...formData,
      document_types: formData.document_types
        .split('\n')
        .map(t => t.trim())
        .filter(t => t.length > 0)
    };

    if (editingCategory) {
      saveMutation.mutate({ id: editingCategory.id, data });
    } else {
      saveMutation.mutate(data);
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        code: category.code,
        name: category.name,
        description: category.description || '',
        document_types: (category.document_types || []).join('\n'),
        display_order: category.display_order || 0
      });
    } else {
      setEditingCategory(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        document_types: '',
        display_order: categories.length + 1
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      document_types: '',
      display_order: 0
    });
  };

  const handleDelete = (category) => {
    setCategoryToDelete(category);
    setConfirmMessage(`Tem certeza que deseja remover "${category.name}"?`);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      deleteMutation.mutate(categoryToDelete.id);
    }
    setShowConfirmModal(false);
    setCategoryToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setCategoryToDelete(null);
  };

  const handleOrder = async (category, direction) => {
    const currentIndex = categories.findIndex(c => c.id === category.id);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= categories.length) return;

    const targetCategory = categories[targetIndex];

    try {
      // Inverter ordens de exibição
      const catOrder = category.display_order ?? currentIndex;
      const targetOrder = targetCategory.display_order ?? targetIndex;

      await Promise.all([
        fiscalCategoriesService.updateCategory(category.id, { display_order: targetOrder }),
        fiscalCategoriesService.updateCategory(targetCategory.id, { display_order: catOrder })
      ]);

      queryClient.invalidateQueries(['fiscal-categories']);
      toast.success('Ordem atualizada!');
    } catch (error) {
      toast.error('Erro ao reordenar');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-secondary-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            Gerenciar Categorias de Documentos
          </h1>
          <p className="text-secondary-600 mt-1">
            Cadastre e organize as categorias de documentos fiscais
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="btn-primary"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nova Categoria
        </button>
      </div>

      {/* Lista de categorias */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {categories.length === 0 ? (
          <div className="p-12 text-center">
            <FolderOpen className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-secondary-500">Nenhuma categoria cadastrada</p>
          </div>
        ) : (
          <div className="divide-y divide-secondary-200">
            {categories.map((category, index) => (
              <div key={category.id} className="p-4 hover:bg-secondary-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleOrder(category, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleOrder(category, 'down')}
                      disabled={index === categories.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-secondary-900">{category.name}</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {category.code}
                      </span>
                    </div>
                    <div className="text-sm text-secondary-500 mt-1">
                      {category.document_types?.length || 0} tipos de documentos
                    </div>
                    {category.description && (
                      <p className="text-sm text-secondary-400 mt-1">{category.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openModal(category)}
                    className="p-2 text-blue-600 hover:text-blue-900"
                    title="Editar"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    className="p-2 text-red-600 hover:text-red-900"
                    title="Remover"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Criar/Editar */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={closeModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-secondary-900">
                      {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-secondary-400 hover:text-secondary-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {!editingCategory && (
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                          Código *
                        </label>
                        <input
                          type="text"
                          value={formData.code}
                          onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))}
                          className="input"
                          placeholder="Ex: CONTRATO_SOCIEDADE"
                          required
                          disabled={editingCategory}
                        />
                        <p className="mt-1 text-xs text-secondary-500">
                          Código único (sem espaços, usar underscore)
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Nome da Categoria *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="input"
                        placeholder="Ex: Contratos de Sociedade"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="input"
                        rows="2"
                        placeholder="Descrição da categoria..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Tipos de Documentos
                      </label>
                      <textarea
                        value={formData.document_types}
                        onChange={(e) => setFormData(prev => ({ ...prev, document_types: e.target.value }))}
                        className="input"
                        rows="4"
                        placeholder="Um tipo por linha...&#10;Ex:&#10;Contrato de Fundação&#10;Alteração Contratual&#10;Estatuto Social"
                      />
                      <p className="mt-1 text-xs text-secondary-500">
                        Digite um tipo de documento por linha
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Ordem de Exibição
                      </label>
                      <input
                        type="number"
                        value={formData.display_order}
                        onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                        className="input"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-secondary-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saveMutation.isLoading}
                    className="btn-primary sm:ml-3 sm:w-auto"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saveMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary mt-3 sm:mt-0 sm:w-auto"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          message={confirmMessage}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default FiscalCategoriesManager;
